/**
 * The one owner of the row → Money step (BL-2147; ADR 060 — Money Type § 2.3):
 * a stored amount paired with its OWN row's currency. `encodeRowMoney` (the
 * row → MoneyWire encoder) composes it, and the bidding engine's reads of
 * `bids.amount` / `max_bids.max_amount` call it directly.
 */
import type Decimal from 'decimal.js';

import { isSupportedCurrency } from './currency';
import { Money } from './money';

/**
 * Pair a stored amount (a Decimal from a `moneyNumeric` column, or the exact
 * decimal string a raw-SQL read of one returns) with its own row's
 * currency. The row's currency — not the lot's — is what makes a mismatched
 * pairing throw `CurrencyMismatchError` at the engine's first comparison.
 *
 * The DB CHECKs only enforce `^[A-Z]{3}$`, so an unsupported code is reachable;
 * it throws rather than guessing a currency.
 *
 * @param amount - The stored amount.
 * @param currency - The same row's ISO 4217 `currency`.
 * @returns The Money.
 * @throws TypeError when the row's currency is not a supported currency.
 */
export function storedMoney(amount: Decimal | string, currency: string): Money {
    if (!isSupportedCurrency(currency)) {
        // The `row carries unsupported currency` wording is what every row → Money
        // reader (and its tests) has always thrown; the owner keeps it.
        throw new TypeError(`row carries unsupported currency "${currency}"`);
    }
    return Money.of(amount, currency);
}
